"use client";

import { cn } from "@/lib/cn";
import type { PoiItemProps } from "./PoiListItem";

/**
 * One POI as a card: image above, title and subtitle below.
 *
 * A `<button>` rather than the legacy `<div role="button">` with its hand-rolled
 * Enter/Space handler — which had to `preventDefault` and call `onSelect` itself,
 * and typed around the fact that it was passing no arguments. A real button gets
 * all of that from the platform.
 */
export function PoiCardItem({
  title,
  subtitle,
  imageUrl,
  imageFitClass,
  isActive,
  onSelect,
  onHoverOn,
  onHoverOff,
}: PoiItemProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      onMouseEnter={onHoverOn}
      onMouseLeave={onHoverOff}
      onFocus={onHoverOn}
      onBlur={onHoverOff}
      className={cn(
        "w-full animate-fadeIn cursor-pointer px-2 py-6 text-start transition-all duration-300",
        "hover:bg-[color:var(--primary-opacity-13)]",
        "dark:bg-[#333] dark:hover:bg-[color:var(--primary-opacity-13)]",
        isActive ? "bg-[color:var(--primary-opacity-13)]" : "bg-white"
      )}
    >
      {imageUrl ? (
        // Tenant-uploaded, arbitrary dimensions, same-origin.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          className={cn("h-[108px] w-full", imageFitClass)}
          src={imageUrl}
          alt=""
          loading="lazy"
        />
      ) : (
        <div className="h-[108px] w-full" />
      )}

      <div className="px-2 pt-4">
        <h5
          className="overflow-hidden text-ellipsis whitespace-nowrap text-base font-bold text-black dark:text-off-white"
          title={title}
        >
          {title}
        </h5>
        <p
          className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-black dark:text-off-white"
          title={subtitle}
        >
          {subtitle}
        </p>
      </div>
    </button>
  );
}
